import React, { useMemo } from "react";
import Avatar from "@mui/material/Avatar";
import { Badge, IconButton } from "@mui/material";
import { CommentIcon } from "components/Icons/CommentIcon";
import { conflictsPalette } from "styles/conflictsPalette";
import { AderantTheme, aderantColors } from "@aderant/aderant-react-components";
import { getUserInitials } from "../UserOptionPanel/UserOptionPanel";

export interface CardProps {
    name: string | null;
    time: string | null;
    message: string | null;
    count?: number;
    isCurrentUser?: boolean;
}

const CommentCard = ({ name, time, message, count, isCurrentUser = false }: CardProps): JSX.Element => {
    // Ensure count is a valid positive number
    const validCount = typeof count === "number" && count > 0 ? count : undefined;

    // Memoize expensive computations and objects that cause re-renders
    const userInitials = useMemo(() => getUserInitials(name || undefined), [name]);
    
    const avatarStyles = useMemo(() => ({
        backgroundColor: isCurrentUser ? aderantColors.Ahsoka : aderantColors.MillenniumFalcon,
        color: isCurrentUser ? aderantColors.Stormtrooper : aderantColors.DarthVaderAlt,
        fontSize: AderantTheme.typography.pxToRem(14),
        fontWeight: "600"
    }), [isCurrentUser]);

    const badgeStyles = useMemo(() => ({
        "& .MuiBadge-badge": {
            fontSize: AderantTheme.typography.pxToRem(12),
            height: "12px",
            minWidth: "12px",
            paddingBottom: "5px",
            backgroundColor: "transparent",
            left: "50%",
            color: conflictsPalette.text.white
        }
    }), []);

    const iconButtonStyles = useMemo(() => ({
        padding: 0,
        "&:hover": {
            backgroundColor: "transparent"
        }
    }), []);

    return (
        <div 
            className="comment-card" 
            role="article" 
            aria-label="Comment card"
        >
            <Avatar 
                className="comment-avatar"
                sx={avatarStyles} 
                alt={`${name || 'Unknown user'} avatar`}
            >
                {userInitials}
            </Avatar>
            <div className="comment-content">
                <div className="comment-header">
                    <span className="comment-name">{name || "Unknown user"}</span>
                    {time && <span className="comment-time">{time}</span>}
                </div>
                {message && <div className="comment-message">{message}</div>}
            </div>
            {validCount && (
                <IconButton aria-label={`${validCount} comments`} sx={iconButtonStyles} disableRipple>
                    <Badge
                        badgeContent={validCount}
                        className="chat-badge"
                        sx={badgeStyles}
                    />
                    <CommentIcon width={24} height={24} />
                </IconButton>
            )}
        </div>
    );
};

export default CommentCard;
